import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { MessageBar } from 'office-ui-fabric-react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import clsx from 'clsx';
import { addHours, format } from 'date-fns';
import { can } from '../../utils/auth';
import { isReviewerOfDimensionList } from '../../utils';
import Link from '../Link';
import styles from './DimensionListActionRequired.style';

const DimensionListActionRequired = ({ listId, hideCta, className: classNameProp, ...other }) => {
  const className = clsx(styles.root, classNameProp);
  const dimensionList = useSelector(state => get(state, `entities.dimensionLists.${listId}`, {}));
  const latestActivity = useSelector(state => {
    const activities = orderBy(
      Object.values(get(state, `entities.activity`, {})).filter(
        activity => activity.resource === listId,
      ),
      ['timestamp'],
      ['desc'],
    );
    return activities.length ? activities[0] : null;
  });
  const { status } = dimensionList;

  let summary;
  let response;
  let ctaLink;
  let ctaText;
  let dueByDate;

  const initiatedName = can('viewActivityInitiatedBy')
    ? latestActivity?.initiatedBy?.name
    : 'Microsoft';

  if (latestActivity)
    dueByDate = format(addHours(new Date(latestActivity?.timestamp), 72), 'MM/dd/yyyy');

  if (status === 'Pending') {
    summary = ` submitted edits for approval. Transaction processing for associated contracts are paused until the edit is approved. Please review changes by `;
    response = ` Reviewer Justification: ${latestActivity?.newData.reviewerJustification}`;
  }
  if (status === 'Rejected') {
    summary = ` rejected your edits. Please review and resubmit or cancel your edits.`;
    response = ` Response: ${latestActivity?.newData.reviewerJustification}`;
  }

  return !summary ? (
    <></>
  ) : (
    <MessageBar
      isMultiline
      messageBarIconProps={{ iconName: 'Error' }}
      className={className}
      {...other}
    >
      <div>
        <Text as="p" className={styles.actionRequiredTitle}>
          Action required
        </Text>
      </div>

      {isReviewerOfDimensionList(dimensionList) ? (
        <Text as="b" inline className={styles.actionRequiredBody}>
          <div className={styles.actionText}>
            <b>{initiatedName}</b> {summary}
            {status === 'Pending' ? <b>{dueByDate}</b> : <> </>}
          </div>
          <li>{response}</li>
        </Text>
      ) : (
        <Text as="b" inline className={styles.actionRequiredBody}>
          This list is currently in revision and is subject to additional reviews and changes.
        </Text>
      )}

      {!hideCta && ctaLink && ctaText && (
        <Link href={ctaLink} className={styles.reviewButton}>
          {ctaText}
          <FontIcon
            iconName="ChevronRight"
            ariaLabel="TextDocument"
            className={styles.chevronRight}
          />
        </Link>
      )}
    </MessageBar>
  );
};

DimensionListActionRequired.propTypes = {
  listId: PropTypes.string.isRequired,
  hideCta: PropTypes.bool,
  className: PropTypes.string,
};

DimensionListActionRequired.defaultProps = {
  hideCta: false,
  className: '',
};

export default DimensionListActionRequired;
